import {
  EXPIRED_MULTIPLIER,
  FREEMIUM_TYPES,
  FREEMIUM_URGENCY_TABLE,
  PROJECT_TYPES,
  PROJECT_URGENCY_TABLE,
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_URGENCY_TABLE,
} from '@/sections/customerManagement/constants/dogPile';
import {
  DogPileCategory,
  TimeRemaining,
  UrgencyTier,
} from '@/sections/customerManagement/types/dogPile.types';
import { CustomerTypeValue } from '@/types';

/**
 * Calculates the time remaining from a subscription/project end date.
 * Returns null if no end date is provided.
 */
export function calculateTimeRemaining(
  endDate: string | undefined
): TimeRemaining | null {
  if (!endDate) return null;

  let date: Date;
  try {
    date = new Date(endDate);
    if (isNaN(date.getTime())) return null;
  } catch {
    return null;
  }

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const dayMs = 1000 * 60 * 60 * 24;
  const isExpired = diffMs < 0;
  const days = isExpired
    ? -Math.ceil(Math.abs(diffMs) / dayMs)
    : Math.ceil(diffMs / dayMs);

  let label: string;
  if (isExpired) {
    const absDays = Math.abs(days);
    if (absDays === 1) {
      label = 'Expired 1d ago';
    } else if (absDays < 7) {
      label = `Expired ${absDays}d ago`;
    } else if (absDays < 30) {
      const weeks = Math.floor(absDays / 7);
      label = `Expired ${weeks}w ago`;
    } else {
      const months = Math.floor(absDays / 30);
      label = `Expired ${months}mo ago`;
    }
  } else if (days === 0) {
    label = 'Expires today';
  } else if (days === 1) {
    label = '1 day';
  } else if (days < 7) {
    label = `${days} days`;
  } else if (days < 30) {
    const weeks = Math.floor(days / 7);
    label = `${weeks} week${weeks > 1 ? 's' : ''}`;
  } else if (days < 365) {
    const months = Math.floor(days / 30);
    label = `${months} month${months > 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(days / 365);
    label = `${years} year${years > 1 ? 's' : ''}`;
  }

  return { days, label, isExpired };
}

/**
 * Returns the urgency multiplier and tier for a customer
 * based on time remaining and their category.
 */
export function getUrgencyMultiplier(
  timeRemaining: TimeRemaining | null,
  category: DogPileCategory
): { multiplier: number; tier: UrgencyTier } {
  if (!timeRemaining) {
    return { multiplier: 1.0, tier: 'HEALTHY' };
  }

  const table =
    category === 'subscriptions'
      ? SUBSCRIPTION_URGENCY_TABLE
      : category === 'projects'
        ? PROJECT_URGENCY_TABLE
        : FREEMIUM_URGENCY_TABLE;

  const absDays = Math.abs(timeRemaining.days);

  for (const entry of table) {
    if (absDays <= entry.maxDays) {
      return { multiplier: entry.multiplier, tier: entry.tier };
    }
  }

  return { multiplier: 1.0, tier: 'HEALTHY' };
}

/**
 * Calculates the priority score for a customer.
 * Formula: (10 - evmScore) * urgencyMultiplier * (expiredMultiplier if expired)
 */
export function calculatePriorityScore(
  evmScore: number,
  urgencyMultiplier: number,
  isExpired: boolean
): number {
  const baseScore =
    (10 - Math.max(0, Math.min(10, evmScore))) * urgencyMultiplier;
  return isExpired ? baseScore * EXPIRED_MULTIPLIER : baseScore;
}

/**
 * Determines the urgency tier based on the computed priority score and
 * whether the account is expired. Used for display purposes only.
 */
export function getUrgencyTierFromScore(
  urgencyTier: UrgencyTier,
  isExpired: boolean
): UrgencyTier {
  if (isExpired) return 'CRITICAL';
  return urgencyTier;
}

/**
 * Maps a CustomerTypeValue to a DogPileCategory.
 * Returns null for types not categorized into the dog pile view
 * (BUG_BOUNTY, DELETE, etc.).
 */
export function categorizeCustomerType(
  type: CustomerTypeValue | undefined
): DogPileCategory | null {
  if (!type) return null;

  if ((SUBSCRIPTION_TYPES as readonly string[]).includes(type)) {
    return 'subscriptions';
  }

  if ((FREEMIUM_TYPES as readonly string[]).includes(type)) {
    return 'freemium';
  }

  if ((PROJECT_TYPES as readonly string[]).includes(type)) {
    return 'projects';
  }

  return null;
}
